import React from "react";

import { UnauthenticatedPath } from "@Constants";
import { isDevelopment } from "@Utils";
import { Switch } from "react-router-dom";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";

import AppRoute from "@Components/shared/AppRoute";

/**
 * Context Providers
 */
import ContextProvider from "@Context/provider";
import { HelmetProvider } from "react-helmet-async";
import { QueryClientProvider } from "@tanstack/react-query";
import { ActivityUploadProvider } from "@Components/activity/context/activityUploadProvider";

/**
 * Layouts
 */
import GroupsLayout from "@Components/layouts/GroupsLayout";
import GuestLayout from "@Components/layouts/GuestLayout";
import MainLayout from "@Components/layouts/MainLayout";
import ProfileLayout from "@Components/layouts/ProfileLayout";

/**
 * Pages
 */
import appQueryClient from "@AppQueryClient";
import AccountDetailsPage from "@Components/pages/AccountDetailsPage";
import ActivateLegacyPage from "@Components/pages/ActivateLegacyPage";
import ActivityPage from "@Components/pages/ActivityPage";
import AssessmentsPage from "@Components/pages/AssessmentsPage";
import CompliancePage from "@Components/pages/CompliancePage";
import ConnectionsPage from "@Components/pages/ConnectionsPage";
import ConsumablesEditPage from "@Components/pages/ConsumablesEditPage";
import DownloadsPage from "@Components/pages/DownloadsPage";
import SupplySuitePage from "@Components/pages/SupplySuitePage";
import GroupsPage from "@Components/pages/GroupsPage";
import UpdatedGroupsPage from "@Components/pages/UpdatedGroupsPage";
import LibraryPage from "@Components/pages/LibraryPage";
import LicensesPage from "@Components/pages/LicensesPage";
import LoginPage from "@Components/pages/LoginPage";
import NotFoundPage from "@Components/pages/NotFoundPage";
import PasswordResetPage from "@Components/pages/PasswordResetPage";
import ProfilePage from "@Components/pages/ProfilePage";
import RegisterPage from "@Components/pages/RegisterPage";
import ResetPasswordPage from "@Components/pages/ResetPasswordPage";
import RoleEditorPage from "@Components/pages/RoleEditorPage";
import AiAssistantPage from "@Components/pages/AiAssistantPage";
import WebcamViewerPage from "@Components/pages/WebcamViewerPage";

// TODO: use lazy loading for these pages
import QuestionnairesPage from "@Components/pages/QuestionnairesPage";
import JSONValidatorPage from "@Components/pages/JSONValidatorPage";

/**
 * Lazy Pages
 * - These pages are lazy loaded so that we can exclude their dependencies from
 * the main vendor file.
 *
 * This is not currently implemented
 *
 * @note we need to make additional changes in webpack for this to work fully
 * @see: https://react.dev/reference/react/lazy
 */
// const JSONValidatorPage = lazy(() => import("@Components/pages/JSONValidatorPage"));

export const routes = (
    <QueryClientProvider client={appQueryClient}>
        <HelmetProvider>
            <ActivityUploadProvider>
                <ContextProvider>
                    <Switch>
                        <AppRoute layout={ProfileLayout} prefetch exact path="/" component={ProfilePage} />
                        <AppRoute layout={GroupsLayout} prefetch exact path="/groups-legacy" component={GroupsPage} />
                        <AppRoute layout={MainLayout} prefetch exact path="/groups" component={UpdatedGroupsPage} />

                        <AppRoute layout={MainLayout} prefetch exact path="/activity" component={ActivityPage} />
                        <AppRoute
                            layout={MainLayout}
                            prefetch
                            exact
                            path="/questionnaires"
                            component={QuestionnairesPage}
                        />
                        <AppRoute layout={MainLayout} prefetch exact path="/imaging" component={WebcamViewerPage} />

                        <AppRoute layout={MainLayout} prefetch exact path="/account" component={AccountDetailsPage} />
                        <AppRoute layout={MainLayout} prefetch exact path="/assessments" component={AssessmentsPage} />
                        <AppRoute layout={MainLayout} prefetch exact path="/roles" component={RoleEditorPage} />
                        <AppRoute layout={MainLayout} prefetch exact path="/video-library" component={LibraryPage} />
                        <AppRoute
                            layout={MainLayout}
                            prefetch
                            path="/edit-consumables"
                            component={ConsumablesEditPage}
                        />
                        <AppRoute layout={MainLayout} prefetch path="/supply-suite" component={SupplySuitePage} />
                        <AppRoute layout={MainLayout} prefetch path="/connections" component={ConnectionsPage} />
                        <AppRoute layout={MainLayout} prefetch path="/compliance" component={CompliancePage} />
                        <AppRoute layout={MainLayout} prefetch path="/downloads" component={DownloadsPage} />
                        <AppRoute layout={MainLayout} prefetch path="/ai-assistant" component={AiAssistantPage} />
                        <AppRoute layout={MainLayout} prefetch path="/licenses" component={LicensesPage} />
                        <AppRoute layout={MainLayout} path="/json-validator" component={JSONValidatorPage} />

                        <AppRoute
                            layout={GuestLayout}
                            exact
                            path={"/" + UnauthenticatedPath.Login}
                            component={LoginPage}
                        />
                        <AppRoute
                            layout={GuestLayout}
                            path={"/" + UnauthenticatedPath.Register}
                            component={RegisterPage}
                        />
                        <AppRoute
                            layout={GuestLayout}
                            exact
                            path={"/" + UnauthenticatedPath.ResetPassword}
                            component={ResetPasswordPage}
                        />
                        <AppRoute
                            layout={GuestLayout}
                            path={"/" + UnauthenticatedPath.PasswordReset}
                            component={PasswordResetPage}
                        />
                        <AppRoute
                            layout={GuestLayout}
                            exact
                            path={"/" + UnauthenticatedPath.Activate}
                            component={ActivateLegacyPage}
                        />
                        <AppRoute layout={GuestLayout} path="*" component={NotFoundPage} statusCode={404} />
                    </Switch>
                </ContextProvider>
            </ActivityUploadProvider>
        </HelmetProvider>
        {isDevelopment() ? <ReactQueryDevtools initialIsOpen={false} /> : null}
    </QueryClientProvider>
);
